import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { zOverModalBanner } from '@/shared/constants/zIdxs';
import { track } from '@/shared/utils/track';
import {
  fetchDeployedRelease,
  isRealRelease,
  reloadPage,
} from './deployedRelease';

const FOREGROUND_THROTTLE_MS = 60_000;
const INTERVAL_MS = 5 * 60_000;

// Stale-bundle detection for long-lived tabs. The reload is always the
// user's — a half-typed form or a pending money action must never be torn
// down underneath them. Off on localhost, where the dev server's release is
// the working tree.
export default function ReleaseCheckProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const running = window.RELEASE;
    if (stale || import.meta.env.VITE_IS_LOCALHOST || !isRealRelease(running))
      return;
    let disposed = false;
    let inFlight = false;
    // The bundle is fresh at mount by definition — the first foreground
    // check waits out one throttle window.
    let lastCheckAt = Date.now();

    const check = async () => {
      if (inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      lastCheckAt = Date.now();
      try {
        const deployed = await fetchDeployedRelease();
        if (!disposed && isRealRelease(deployed) && deployed !== running) {
          track('release_banner_shown');
          setStale(true);
        }
      } catch {
        // Network blip — the next foreground event or interval tick retries.
      } finally {
        inFlight = false;
      }
    };
    const onForeground = () => {
      if (Date.now() - lastCheckAt >= FOREGROUND_THROTTLE_MS) void check();
    };
    window.addEventListener('focus', onForeground);
    document.addEventListener('visibilitychange', onForeground);
    const interval = setInterval(() => void check(), INTERVAL_MS);

    return () => {
      disposed = true;
      clearInterval(interval);
      window.removeEventListener('focus', onForeground);
      document.removeEventListener('visibilitychange', onForeground);
    };
  }, [stale]);

  return (
    <>
      {children}
      {stale && createPortal(<Banner />, document.body)}
    </>
  );
}

function Banner() {
  return (
    <div
      role="status"
      style={{ zIndex: zOverModalBanner }}
      className="fixed inset-x-0 top-0 flex items-center justify-center gap-m border-b border-blue-03 bg-blue-01 px-4 py-2 text-body-sm font-medium text-blue-default"
    >
      A new version of Score Arena is available
      <button
        type="button"
        onClick={() => {
          track('release_reload_clicked');
          reloadPage();
        }}
        className="cursor-pointer font-bold underline underline-offset-2"
      >
        Reload
      </button>
    </div>
  );
}
